import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Tag, Search, Layers } from 'lucide-react';
import { apiClient } from '../../api/client';
import { AShareStockTagsBadge, StockTagItem } from './AShareStockTagsBadge';
import { ASHARE_BILINGUAL } from '../../shared/constants/bilingualTitles';

export interface StockPatternTagItem {
  symbol: string;
  name?: string;
  trade_date: string;
  status: string;
  tags: StockTagItem[];
  metrics?: {
    close?: string;
    pct_change?: string;
    rs_diff?: string;
    v_ratio?: string;
    amp_10_avg?: string;
    ma5?: string;
    ma10?: string;
    ma20?: string;
    ma60?: string;
  };
}

export interface ASharePatternTagsViewProps {
  colorMode?: 'mainland' | 'international';
  tradeDate?: string;
}

const DEFAULT_INSPECTOR_SYMBOLS = ['600519', '000858', '300750', '601318', '002594'];

const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'fresh':
      return 'var(--color-emerald, #10b981)';
    case 'stale':
      return 'var(--color-warning, #f59e0b)';
    case 'empty':
      return 'var(--text-muted, #94a3b8)';
    case 'disconnected':
      return 'var(--color-danger, #ef4444)';
    case 'loading':
    default:
      return 'var(--accent-cyan, #06b6d4)';
  }
};

export const ASharePatternTagsView: React.FC<ASharePatternTagsViewProps> = ({
  tradeDate,
}) => {
  const [searchSymbol, setSearchSymbol] = useState<string>('600519');
  const [singleResult, setSingleResult] = useState<StockPatternTagItem | null>(null);
  const [singleLoading, setSingleLoading] = useState<boolean>(false);

  const [batchData, setBatchData] = useState<StockPatternTagItem[]>([]);
  const [batchLoading, setBatchLoading] = useState<boolean>(false);
  const [activeCategory, setActiveCategory] = useState<string>('ALL');

  const [resolvedTradeDate, setResolvedTradeDate] = useState<string>(tradeDate || '');
  const [viewStatus, setViewStatus] = useState<string>('loading');

  useEffect(() => {
    if (tradeDate) {
      setResolvedTradeDate(tradeDate);
    }
  }, [tradeDate]);

  const querySingleStock = useCallback(async (sym: string) => {
    if (!sym || sym.trim().length === 0) return;
    setSingleLoading(true);
    try {
      const dateParam = tradeDate ? `&trade_date=${encodeURIComponent(tradeDate)}` : '';
      const res = await apiClient.get<{
        status: string;
        trade_date: string;
        symbol: string;
        name?: string;
        tags: StockTagItem[];
        metrics?: Record<string, string>;
      }>(`/api/v1/market/a-share/pattern-tags/inspect?symbol=${encodeURIComponent(sym.trim())}${dateParam}`);

      if (res) {
        setSingleResult({
          symbol: res.symbol,
          name: res.name || sym,
          trade_date: res.trade_date,
          status: res.status,
          tags: res.tags || [],
          metrics: res.metrics,
        });
        if (res.trade_date && !tradeDate) {
          setResolvedTradeDate(res.trade_date);
        }
      }
    } catch (err) {
      setSingleResult(null);
    } finally {
      setSingleLoading(false);
    }
  }, [tradeDate]);

  const fetchBatchMatrix = useCallback(async () => {
    setBatchLoading(true);
    try {
      const dateParam = tradeDate ? `&trade_date=${encodeURIComponent(tradeDate)}` : '';
      const res = await apiClient.get<{
        status: string;
        trade_date: string;
        items: StockPatternTagItem[];
      }>(`/api/v1/market/a-share/pattern-tags/batch?limit=30${dateParam}`);

      if (res) {
        if (res.items) {
          setBatchData(res.items);
        }
        if (res.trade_date) {
          setResolvedTradeDate(res.trade_date);
        }
        if (res.status) {
          setViewStatus(res.status);
        }
      }
    } catch (err) {
      setBatchData([]);
      setViewStatus('disconnected');
    } finally {
      setBatchLoading(false);
    }
  }, [tradeDate]);

  useEffect(() => {
    querySingleStock(searchSymbol);
    fetchBatchMatrix();
  }, [querySingleStock, fetchBatchMatrix]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    querySingleStock(searchSymbol);
  };

  const filteredBatchItems = useMemo(() => {
    if (activeCategory === 'ALL') return batchData;
    return batchData.filter((item) => {
      if (!item.tags) return false;
      return item.tags.some((t) => t.category === activeCategory || t.code === activeCategory);
    });
  }, [batchData, activeCategory]);

  return (
    <div className="view-container animate-fade-in" style={{ width: '100%', boxSizing: 'border-box' }}>
      {/* 1. Standardized TSO 2.0 View Header */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top">
            <Tag size={18} style={{ color: 'var(--accent-cyan)' }} />
            <h2 className="bilingual-header-title-zh">{ASHARE_BILINGUAL.patternTags.zh}</h2>
          </div>
          <span className="bilingual-header-subtitle-en">{ASHARE_BILINGUAL.patternTags.en}</span>
          <p className="view-subtitle" style={{ margin: '3px 0 0 0' }}>
            个股量价形态打标检视器、六大量化特征事实看板与均线结构实时分类
          </p>
        </div>

        {/* Global Metadata Badge */}
        <div className="view-actions" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '11px',
          fontFamily: 'var(--font-mono)',
        }}>
          <span style={{ color: getStatusColor(viewStatus), fontWeight: 600 }}>
            ● {viewStatus.toUpperCase()}
          </span>
          <span style={{ color: 'var(--text-muted)' }}>交易日:</span>
          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
            {resolvedTradeDate || tradeDate || '--'}
          </span>
          <span style={{ color: 'var(--text-muted)' }}>|</span>
          <span style={{ color: 'var(--text-muted)' }}>延迟窗口:</span>
          <span style={{ fontWeight: 600, color: 'var(--accent-cyan)' }}>W &ge; 3s</span>
        </div>
      </div>

      {/* Quick Search & Single Stock Inspector Card */}
      <div style={{
        padding: '20px',
        marginBottom: '20px',
        borderRadius: '8px',
        background: 'var(--bg-card)',
        border: '1px solid var(--border-card)',
        boxShadow: 'var(--shadow-card)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Search size={16} className="icon-cyan" /> 单股形态事实穿透检视器 (Symbol Inspector)
          </h2>
          <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              value={searchSymbol}
              onChange={(e) => setSearchSymbol(e.target.value)}
              placeholder="输入6位代码，如 000001"
              maxLength={6}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid var(--border-card)',
                background: 'var(--bg-base)',
                color: 'var(--text-primary)',
                fontSize: '13px',
                fontFamily: 'var(--font-mono)',
                width: '180px',
                outline: 'none',
              }}
            />
            <button
              type="submit"
              disabled={singleLoading}
              style={{
                padding: '6px 16px',
                borderRadius: '6px',
                background: 'var(--accent-cyan)',
                color: 'var(--text-inverse)',
                border: 'none',
                fontWeight: 600,
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              {singleLoading ? '打标中...' : '深度检视'}
            </button>
          </form>
        </div>

        {/* Preset Symbols Quick Picks */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>快速穿透:</span>
          {DEFAULT_INSPECTOR_SYMBOLS.map((s) => (
            <button
              key={s}
              onClick={() => {
                setSearchSymbol(s);
                querySingleStock(s);
              }}
              style={{
                padding: '3px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontFamily: 'var(--font-mono)',
                fontWeight: searchSymbol === s ? 700 : 500,
                background: searchSymbol === s ? 'var(--accent-cyan)' : 'var(--bg-base)',
                color: searchSymbol === s ? 'var(--text-inverse)' : 'var(--text-secondary)',
                border: '1px solid var(--border-card)',
                cursor: 'pointer',
              }}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Single Inspection Result Display */}
        {singleResult ? (
          <div style={{
            padding: '16px',
            borderRadius: '6px',
            background: 'var(--bg-base)',
            border: '1px solid var(--border-card)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                  {singleResult.symbol}
                </span>
                <span style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {singleResult.name || '--'}
                </span>
                <span style={{
                  padding: '1px 6px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontFamily: 'var(--font-mono)',
                  background: singleResult.status === 'fresh' ? 'var(--badge-bullish-bg)' : 'var(--bg-card)',
                  color: getStatusColor(singleResult.status),
                  border: '1px solid var(--border-subtle)',
                }}>
                  {singleResult.status.toUpperCase()}
                </span>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                基准交易日: <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{singleResult.trade_date}</span>
              </div>
            </div>

            {/* Active Tags Badge Cloud */}
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '6px' }}>
                激活的量化形态事实标签 (Active Pattern Tags):
              </div>
              <AShareStockTagsBadge tags={singleResult.tags} maxDisplay={10} />
            </div>

            {/* Quant Metrics Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '10px',
              fontSize: '12px',
              fontFamily: 'var(--font-mono)',
            }}>
              <div style={{ padding: '8px 10px', background: 'var(--bg-card)', borderRadius: '4px', border: '1px solid var(--border-card)' }}>
                <div style={{ color: 'var(--text-muted)', fontSize: '10px' }}>相对强弱 (RS Diff)</div>
                <div style={{ fontWeight: 600, color: 'var(--color-emerald)', fontSize: '13px' }}>
                  {singleResult.metrics?.rs_diff ? `+${singleResult.metrics.rs_diff}%` : '--'}
                </div>
              </div>
              <div style={{ padding: '8px 10px', background: 'var(--bg-card)', borderRadius: '4px', border: '1px solid var(--border-card)' }}>
                <div style={{ color: 'var(--text-muted)', fontSize: '10px' }}>量比系数 (V-Ratio)</div>
                <div style={{ fontWeight: 600, color: 'var(--accent-cyan)', fontSize: '13px' }}>
                  {singleResult.metrics?.v_ratio ? `${singleResult.metrics.v_ratio}x` : '--'}
                </div>
              </div>
              <div style={{ padding: '8px 10px', background: 'var(--bg-card)', borderRadius: '4px', border: '1px solid var(--border-card)' }}>
                <div style={{ color: 'var(--text-muted)', fontSize: '10px' }}>10日均振幅 (Amp10)</div>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '13px' }}>
                  {singleResult.metrics?.amp_10_avg ? `${singleResult.metrics.amp_10_avg}` : '--'}
                </div>
              </div>
              <div style={{ padding: '8px 10px', background: 'var(--bg-card)', borderRadius: '4px', border: '1px solid var(--border-card)' }}>
                <div style={{ color: 'var(--text-muted)', fontSize: '10px' }}>MA5 均线价</div>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '13px' }}>
                  {singleResult.metrics?.ma5 || '--'}
                </div>
              </div>
              <div style={{ padding: '8px 10px', background: 'var(--bg-card)', borderRadius: '4px', border: '1px solid var(--border-card)' }}>
                <div style={{ color: 'var(--text-muted)', fontSize: '10px' }}>MA20 均线价</div>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '13px' }}>
                  {singleResult.metrics?.ma20 || '--'}
                </div>
              </div>
              <div style={{ padding: '8px 10px', background: 'var(--bg-card)', borderRadius: '4px', border: '1px solid var(--border-card)' }}>
                <div style={{ color: 'var(--text-muted)', fontSize: '10px' }}>MA60 均线价</div>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '13px' }}>
                  {singleResult.metrics?.ma60 || '--'}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
            {singleLoading ? '正在通过只读网关分析个股形态与事实打标...' : '暂无数据或标的尚未入库'}
          </div>
        )}
      </div>

      {/* Batch Tag Radar Table Section */}
      <div style={{
        padding: '20px',
        marginBottom: '20px',
        borderRadius: '8px',
        background: 'var(--bg-card)',
        border: '1px solid var(--border-card)',
        boxShadow: 'var(--shadow-card)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Layers size={16} className="icon-cyan" /> 核心标的形态标签实时矩阵 (Batch Pattern Matrix)
            </h2>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              覆盖重点跟踪池标的，由后台纯 Decimal 引擎在闭市后自动完成高精度离线打标
            </div>
          </div>

          {/* Category Filter Pills */}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {[
              { id: 'ALL', label: '全部形态' },
              { id: 'VOLUME', label: '量能类 (VOL)' },
              { id: 'MA', label: '均线类 (MA)' },
              { id: 'VOLATILITY', label: '波动率类' },
              { id: 'RS', label: '相对大盘强弱' },
            ].map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                style={{
                  padding: '4px 10px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: activeCategory === cat.id ? 600 : 400,
                  background: activeCategory === cat.id ? 'var(--accent-cyan)' : 'var(--bg-base)',
                  color: activeCategory === cat.id ? 'var(--text-inverse)' : 'var(--text-secondary)',
                  border: '1px solid var(--border-card)',
                  cursor: 'pointer',
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-card)', background: 'var(--bg-base)' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>代码 / 名称</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>形态标签云 (Quant Tags)</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: 'var(--text-secondary)' }}>相对强弱 (RS Diff)</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: 'var(--text-secondary)' }}>量比系数</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: 'var(--text-secondary)' }}>10日振幅</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: 'var(--text-secondary)' }}>时效状态</th>
              </tr>
            </thead>
            <tbody>
              {filteredBatchItems.length > 0 ? (
                filteredBatchItems.map((it) => (
                  <tr
                    key={it.symbol}
                    style={{
                      borderBottom: '1px solid var(--border-card)',
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-base)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                        {it.symbol}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                        {it.name || '--'}
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <AShareStockTagsBadge tags={it.tags} maxDisplay={4} />
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--color-emerald)' }}>
                      {it.metrics?.rs_diff ? `+${it.metrics.rs_diff}%` : '--'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--accent-cyan)' }}>
                      {it.metrics?.v_ratio ? `${it.metrics.v_ratio}x` : '--'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                      {it.metrics?.amp_10_avg ? `${it.metrics.amp_10_avg}` : '--'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <span style={{
                        padding: '1px 6px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontFamily: 'var(--font-mono)',
                        background: it.status === 'fresh' ? 'var(--badge-bullish-bg)' : 'var(--bg-card)',
                        color: getStatusColor(it.status),
                        border: '1px solid var(--border-subtle)',
                      }}>
                        {it.status.toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    {batchLoading ? '正在批量拉取核心标的形态事实标签...' : '当前分类下暂无匹配标的'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
